import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { getAllFiles, getMainKeysByFile } from "./files";

const namespaces = ["common"];

const mainKeysByFile = getMainKeysByFile();
const { enFiles, esFiles } = getAllFiles();

function isObject(item: any): boolean {
  return item && typeof item === "object" && !Array.isArray(item);
}

// Function to make deep merge of objects (only overrides the keys that come from the source)
function deepMerge(target: any, source: any): any {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key]) && !Array.isArray(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        // If it is a primitive or array, override it
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
}

// Function to automatically find the file that contains a main key
function findLocaleFileForKey(key: string, locale: "en" | "es"): any | null {
  const fileMapping = mainKeysByFile[key];

  if (fileMapping) {
    return fileMapping?.[locale];
  }

  return null;
}

// Flag to ensure that the function is executed only once per application load
let hasAppliedOverrides = false;

/**
 * Applies translation overrides from the backend.
 * Only overrides the keys present in the overrides object,
 * keeping all other translations intact.
 * This function is executed only once per application load.
 *
 * @param overrides - Object containing the translations to override for both languages.
 *   Example:
 *     {
 *       en: { "groups-manager": { "donut-legend": { "free": "Free (Custom)" } } },
 *       es: { "groups-manager": { "donut-legend": { "free": "Sin usar" } } }
 *     }
 */
export function applyLocaleOverrides(overrides: {
  en?: Record<string, any>;
  es?: Record<string, any>;
}): void {
  // Prevent multiple applications
  if (hasAppliedOverrides) {
    return;
  }

  // Apply overrides for English if they exist
  if (overrides.en) {
    Object.keys(overrides.en).forEach(mainKey => {
      const baseTranslations = findLocaleFileForKey(mainKey, "en");

      if (!baseTranslations) {
        console.warn(`No file found for the main key: ${mainKey} (en)`);
        return;
      }

      const baseSection = baseTranslations[mainKey];

      if (!baseSection) {
        console.warn(`The main key "${mainKey}" does not exist in the found locale file (en)`);
        return;
      }

      const mergedSection = deepMerge(baseSection, overrides.en[mainKey]);
      const mergedTranslations = {
        [mainKey]: mergedSection,
      };

      i18n.addResourceBundle("en", "common", mergedTranslations, true, true);
    });
  }

  // Apply overrides for Spanish if they exist
  if (overrides.es) {
    Object.keys(overrides.es).forEach(mainKey => {
      const baseTranslations = findLocaleFileForKey(mainKey, "es");

      if (!baseTranslations) {
        console.warn(`No file found for the main key: ${mainKey} (es)`);
        return;
      }

      const baseSection = baseTranslations[mainKey];

      if (!baseSection) {
        console.warn(`The main key "${mainKey}" does not exist in the found locale file (es)`);
        return;
      }

      const mergedSection = deepMerge(baseSection, overrides.es[mainKey]);
      const mergedTranslations = {
        [mainKey]: mergedSection,
      };

      i18n.addResourceBundle("es", "common", mergedTranslations, true, true);
    });
  }

  // Mark as applied to prevent multiple applications
  hasAppliedOverrides = true;
}

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enFiles,
    },
    es: {
      common: esFiles,
    },
  },
  lng: window?.localStorage?.getItem("locale") ?? "es",
  ns: namespaces,
  defaultNS: "common",
});

export default i18n;
